import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import axios from '../utils/axiosInstance';
import { usePractice } from '../context/PracticeContext';
import { isoDay, formatDeDay } from '../utils/dateRange';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
// Stable identity for the not-yet-loaded case: `sites` is an effect dependency
// below, so a fresh [] per render would re-run that effect on every render.
const NO_SITES = [];

// Ejection seat: backrest + headrest + seat pan, with thrust below. Stroke-only,
// 20x20, no fill — same construction as the navLink icons in Layout.js.
const ejectionSeatIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 3v9H9" />
    <path d="M9 12v2.5" />
    <path d="M13.5 3h3" />
    <path d="M10.5 16.5 9 21" />
    <path d="M13 16.5V21" />
    <path d="m15.5 16.5 1.5 4.5" />
  </svg>
);

// The PVS worker reads `start`/`end` as naive Europe/Berlin wall-clock, which is
// exactly what a datetime-local input produces. So the browser's local time is
// passed through untouched — no UTC conversion anywhere in this file. A user in
// another timezone would therefore send their own wall-clock; every practice and
// every worker sits in Berlin, so that case does not arise.
const toLocalInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${isoDay(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// 'YYYY-MM-DDTHH:MM' -> '01.09.2026 15:37'. The time half is already 24h in the
// input's value regardless of locale, so only the date half needs converting.
const formatDeDateTime = (value) => {
  if (!value) return '';
  const [day, time] = value.split('T');
  return time ? `${formatDeDay(day)} ${time}` : formatDeDay(day);
};

const plusHours = (hours, from = new Date()) => {
  const d = new Date(from.getTime());
  d.setHours(d.getHours() + hours);
  d.setSeconds(0, 0);
  return d;
};

/**
 * PVS maintenance ("Schleudersitz") control in the header.
 *
 * Lets a practice take its PVS offline for a window while it is patched or
 * updated. Renders NOTHING unless the backend reports at least one eligible
 * site: PVS enabled AND the detected system is TheOrg (only the TheOrg worker
 * serves /maintenance/*). task_manager memberships are filtered out server-side.
 *
 * All user-facing copy names the system as the worker reported it ("TheOrg"),
 * carried on each site as `pvs_system`.
 *
 * Deliberately stateless: the worker exposes no way to read a running window
 * back, so this shows the outcome of the request that was just sent and nothing
 * more. It cannot indicate that a practice is currently in maintenance, nor that
 * a future window is pending — that needs a GET /maintenance/status on the worker.
 */
const PvsMaintenanceMenu = () => {
  const { practiceId, availablePractices } = usePractice();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('now'); // 'now' | 'later'
  const [start, setStart] = useState(() => toLocalInput(plusHours(1)));
  const [end, setEnd] = useState(() => toLocalInput(plusHours(2)));
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);

  // Which practices may be ejected. The active practice is part of the question:
  // for a super_admin the backend answers with what THIS practice's widest-reaching
  // owner sees instead of the whole fleet, so switching practice changes the set.
  // For everyone else the answer is their own memberships and the parameter is
  // ignored. Also reloaded when the membership list changes, which is part of the
  // key so a real membership change still refetches.
  //
  // Held in react-query, not local state: every page renders its own <Layout>, so
  // navigating remounts this component. With local state the button vanished on
  // each navigation until the request came back. cacheTime Infinity keeps the
  // answer for the whole session, so after the first load a remount paints the
  // button immediately and any refresh happens in the background.
  const { data: sites = NO_SITES } = useQuery(
    ['pvs-maintenance-sites', practiceId, availablePractices.map((p) => p.practice_id).join(',')],
    async () => {
      const res = await axios.get(`${API_BASE_URL}/test-call/pvs-maintenance/sites`, { params: { practice_id: practiceId } });
      return Array.isArray(res.data) ? res.data : [];
    },
    {
      // Practices not resolved yet; asking now would answer for "no practice".
      enabled: !!practiceId,
      cacheTime: Infinity,
      // No eligible site, no permission, or the call failed: the button simply
      // does not appear. Nothing here is worth interrupting the header for.
      retry: false,
      onError: () => {},
    },
  );

  // Every listed practice starts ticked: the list is already scoped to what this
  // caller may eject from here, and taking all of them down together is the normal
  // reason a group opens this panel. Untick to narrow it.
  useEffect(() => {
    if (!open) return;
    setResults([]);
    setError('');
    setSelected(sites.map((s) => s.practice_id));
    const nextStart = plusHours(1);
    setStart(toLocalInput(nextStart));
    setEnd(toLocalInput(plusHours(2)));
  }, [open, sites]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleSite = (id) => {
    setSelected((prev) => (
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    ));
  };

  // Name the system the way the worker names itself ("TheOrg"), not "PVS" — the
  // practice knows the product, not the acronym. Every eligible site is TheOrg by
  // definition of the gate, so they agree in practice; fall back to "PVS" only if
  // two sites ever disagree or the detected value is blank.
  const pvsName = (() => {
    const names = [...new Set(sites.map((s) => (s.pvs_system || '').trim()).filter(Boolean))];
    return names.length === 1 ? names[0] : 'PVS';
  })();

  const siteName = useCallback(
    (id) => sites.find((s) => s.practice_id === id)?.practice_name || id,
    [sites],
  );

  // One request per site rather than a bulk endpoint: each practice keeps its own
  // membership check and its own worker, so a failure on one must not hide a
  // success on another. Results are reported per site for the same reason.
  const fanOut = async (buildRequest) => {
    if (selected.length === 0) {
      setError('Bitte mindestens eine Praxis auswählen.');
      return;
    }
    setBusy(true);
    setError('');
    setResults([]);
    const settled = await Promise.all(selected.map(async (id) => {
      try {
        const res = await buildRequest(id);
        return {
          practiceId: id,
          success: !!res.data?.success,
          message: res.data?.message || '',
        };
      } catch (err) {
        return {
          practiceId: id,
          success: false,
          message: err.response?.data?.detail || err.message,
        };
      }
    }));
    setResults(settled);
    setBusy(false);
  };

  // 'Sofort' stamps `start` at CLICK time while `end` was picked when the panel
  // opened, so a panel left open past `end` would send end <= start and be
  // rejected. Push `end` out instead of failing on a technicality.
  const handleStart = () => {
    const effectiveStart = mode === 'now' ? toLocalInput(new Date()) : start;
    const effectiveEnd = end > effectiveStart ? end : toLocalInput(plusHours(1));
    return fanOut((id) => axios.post(
      `${API_BASE_URL}/test-call/pvs-maintenance/schedule?practice_id=${encodeURIComponent(id)}`,
      { start: effectiveStart, end: effectiveEnd },
    ));
  };

  const handleStop = () => fanOut((id) => axios.post(
    `${API_BASE_URL}/test-call/pvs-maintenance/stop?practice_id=${encodeURIComponent(id)}`,
  ));

  // A native datetime-local renders in the BROWSER's locale, so an en-US browser
  // shows "09/01/2026, 03:37 PM". `lang="de"` does NOT override that. So the
  // visible field is a read-only text input in German format and the real picker
  // sits on top at opacity 0 — the same overlay DateRangeDrawer.js and
  // CalendarPage.js use for their date fields.
  const dateTimeField = (label, value, setValue) => (
    <div className="pvs-maint-field">
      <span>{label}</span>
      <div className="pvs-maint-datetime">
        {/* Purely visual mirror of the real input below; hidden from the a11y
            tree so a screen reader doesn't announce an unlabeled duplicate. */}
        <input
          type="text"
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          value={formatDeDateTime(value)}
          placeholder="TT.MM.JJJJ HH:MM"
        />
        <span className="pvs-maint-datetime-icon" aria-hidden="true">📅</span>
        <input
          type="datetime-local"
          lang="de"
          aria-label={label}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
    </div>
  );

  if (sites.length === 0) return null;

  return (
    <div ref={wrapRef} className="pvs-maint">
      <button
        type="button"
        className={`pvs-maint-trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${pvsName}-Wartung`}
        title={`${pvsName}-Wartung`}
      >
        {ejectionSeatIcon}
      </button>
      {open && (
        <div className="pvs-maint-panel" role="dialog" aria-label={`${pvsName}-Wartung`}>
          <div className="pvs-maint-header">
            <div className="pvs-maint-title">{pvsName}-Wartung</div>
            <div className="pvs-maint-hint">
              Meldet DeskBot von {pvsName} ab, damit Sie Ihr System in Ruhe warten können.
            </div>
          </div>

          <div className="pvs-maint-modes">
            <button
              type="button"
              className={`pvs-maint-mode${mode === 'now' ? ' is-active' : ''}`}
              onClick={() => setMode('now')}
            >
              Sofort
            </button>
            <button
              type="button"
              className={`pvs-maint-mode${mode === 'later' ? ' is-active' : ''}`}
              onClick={() => setMode('later')}
            >
              Später planen
            </button>
          </div>

          {mode === 'later' && dateTimeField('Beginn', start, setStart)}
          {dateTimeField('Ende', end, setEnd)}

          {sites.length === 1 && sites[0].practice_id !== practiceId && (
            <div className="pvs-maint-sites-label">
              Betrifft: {sites[0].practice_name}
            </div>
          )}

          {sites.length > 1 && (
            <div className="pvs-maint-sites">
              <div className="pvs-maint-sites-label">Praxen</div>
              {sites.map((s) => (
                <label key={s.practice_id} className="pvs-maint-site">
                  <input
                    type="checkbox"
                    checked={selected.includes(s.practice_id)}
                    onChange={() => toggleSite(s.practice_id)}
                  />
                  <span>{s.practice_name}</span>
                </label>
              ))}
            </div>
          )}

          {error && <div className="pvs-maint-error">{error}</div>}

          <div className="pvs-maint-actions">
            <button
              type="button"
              className="pvs-maint-btn pvs-maint-btn-primary"
              onClick={handleStart}
              disabled={busy}
            >
              {busy ? 'Bitte warten...' : 'Wartung starten'}
            </button>
            <button
              type="button"
              className="pvs-maint-btn"
              onClick={handleStop}
              disabled={busy}
            >
              Wieder online
            </button>
          </div>

          {results.length > 0 && (
            <div className="pvs-maint-results">
              {results.map((r) => (
                <div
                  key={r.practiceId}
                  className={`pvs-maint-result${r.success ? ' is-ok' : ' is-fail'}`}
                >
                  {siteName(r.practiceId)}: {r.success ? 'übernommen' : r.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PvsMaintenanceMenu;
